/**
 * Driver portal — online/offline, auto-assigned bus, pickups, en-route ETA & passenger alerts.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';
import { localTodayStr } from '../lib/date';
import { phoneTelHref } from '../lib/phone';
import { formatTimeAmPm, isTripSlotActiveOnDate } from '../lib/tripSlots';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

function parseTimeToDateToday(timeStr, now = new Date()) {
  if (!timeStr) return null;
  const parts = String(timeStr).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const d = new Date(now);
  d.setHours(hh, mm, 0, 0);
  return d;
}

function formatDepartureCountdown(diffMs) {
  const positiveMs = Math.max(0, diffMs);
  const totalMinutes = Math.ceil(positiveMs / 60000);
  if (totalMinutes < 60) {
    return `Departure in ${totalMinutes} minutes`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `Departure in ${hours} hours ${minutes} minutes`;
}

export default function DriverPortal() {
  const { profile, signOutUser, refresh } = useAuth();
  const navigate = useNavigate();
  const tripDate = localTodayStr();

  const [myBus, setMyBus] = useState(null);
  const [unavailableBus, setUnavailableBus] = useState(null);
  const [activeTripSlot, setActiveTripSlot] = useState(null);
  const [goingRows, setGoingRows] = useState([]);
  const [waitlistRows, setWaitlistRows] = useState([]);
  const [completingRoute, setCompletingRoute] = useState(false);
  const [startingRoute, setStartingRoute] = useState(false);
  const [startOdometer, setStartOdometer] = useState('');
  const [endOdometer, setEndOdometer] = useState('');
  const [etaMinutes, setEtaMinutes] = useState('');
  const [issueMessage, setIssueMessage] = useState('');
  const [sendingIssue, setSendingIssue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  const routeEnRoute = !!myBus?.route_started_at;
  const hasWaitlist = waitlistRows.length > 0;

  const pickupSummary = useMemo(() => {
    const people = goingRows.reduce((sum, r) => sum + (r.party_total ?? 1), 0);
    const seats = goingRows.reduce((sum, r) => sum + (r.seat_count ?? 1), 0);
    return { passengers: goingRows.length, people, seats };
  }, [goingRows]);

  const load = useCallback(async () => {
    if (!profile?.id || !profile.org_id) return;
    setError('');

    const { data: assigned, error: busErr } = await supabase
      .from('buses')
      .select(
        'id, label, plate_number, route_name, is_available, driver_id, route_started_at, bus_type, service_area, seat_capacity, last_odometer, current_trip_slot_id',
      )
      .eq('org_id', profile.org_id)
      .eq('driver_id', profile.id)
      .maybeSingle();

    if (busErr) {
      setError(busErr.message);
      return;
    }

    if (assigned && !assigned.is_available) {
      setMyBus(null);
      setUnavailableBus(assigned);
      setGoingRows([]);
      setWaitlistRows([]);
      setActiveTripSlot(null);
      return;
    }

    setUnavailableBus(null);
    setMyBus(assigned);

    let activeSlot = null;
    if (assigned?.id) {
      if (assigned.current_trip_slot_id) {
        const { data: slotRow } = await supabase
          .from('org_trip_slots')
          .select('id, label, start_time')
          .eq('id', assigned.current_trip_slot_id)
          .maybeSingle();
        activeSlot = slotRow;
      } else {
        const { data: slotRows } = await supabase
          .from('org_trip_slots')
          .select('id, label, start_time, trip_date, recurrence_type, days_of_week, day_of_month')
          .eq('org_id', profile.org_id)
          .order('start_time', { ascending: true });
        const activeSlots = (slotRows || []).filter((slot) => isTripSlotActiveOnDate(slot, tripDate));
        activeSlot = activeSlots[0] ?? null;
      }
      setActiveTripSlot(activeSlot);

      let query = supabase
        .from('daily_going')
        .select(
          'id, passenger_id, guest_passenger_id, going, picked_up, assignment_status, party_total, party_infants, seat_count, trip_slot_id',
        )
        .eq('bus_id', assigned.id)
        .eq('trip_date', tripDate)
        .eq('going', true)
        .eq('picked_up', false);

      if (activeSlot?.id) {
        query = query.eq('trip_slot_id', activeSlot.id);
      }

      const { data: rows, error: gErr } = await query;

      if (gErr) {
        setError(gErr.message);
        return;
      }
      const base = rows || [];
      const confirmed = base.filter((r) => r.assignment_status === 'confirmed');
      const waitlisted = base.filter((r) => r.assignment_status === 'waitlisted');
      const ids = base.map((r) => r.passenger_id).filter(Boolean);
      const guestIds = base.map((r) => r.guest_passenger_id).filter(Boolean);
      let nameMap = {};
      let guestMap = {};
      if (ids.length) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, full_name, pickup_area, pickup_address, phone')
          .in('id', ids);
        nameMap = Object.fromEntries((profs || []).map((p) => [p.id, p]));
      }
      if (guestIds.length) {
        const { data: guests } = await supabase
          .from('org_guest_passengers')
          .select('id, full_name, pickup_area, pickup_address, phone')
          .in('id', guestIds);
        guestMap = Object.fromEntries((guests || []).map((g) => [g.id, g]));
      }
      const enrich = (r) => {
        const prof = r.passenger_id ? nameMap[r.passenger_id] : null;
        const guest = r.guest_passenger_id ? guestMap[r.guest_passenger_id] : null;
        const src = prof || guest;
        return {
          ...r,
          displayName: src?.full_name || 'Passenger',
          pickupArea: src?.pickup_area || '',
          pickupAddress: src?.pickup_address || '',
          phone: src?.phone || '',
          isGuest: !!r.guest_passenger_id,
        };
      };
      setGoingRows(confirmed.map(enrich));
      setWaitlistRows(waitlisted.map(enrich));
    } else {
      setActiveTripSlot(null);
      setGoingRows([]);
      setWaitlistRows([]);
    }
  }, [profile?.id, profile?.org_id, tripDate]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (!myBus?.id) return undefined;
    const t = setInterval(() => {
      load();
    }, 20000);
    return () => clearInterval(t);
  }, [myBus?.id, load]);

  const programDepartureTime = activeTripSlot?.start_time ?? null;

  useEffect(() => {
    if (!myBus?.id || !programDepartureTime) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [myBus?.id, programDepartureTime]);

  const departureDate = useMemo(() => {
    if (!programDepartureTime) return null;
    return parseTimeToDateToday(programDepartureTime, new Date(nowMs));
  }, [programDepartureTime, nowMs]);

  const isDepartureTimeWindow =
    !!departureDate && !myBus?.route_started_at && nowMs >= departureDate.getTime() && nowMs < departureDate.getTime() + 60 * 1000;

  const countdownText = useMemo(() => {
    if (!departureDate || myBus?.route_started_at) return null;
    return formatDepartureCountdown(departureDate.getTime() - nowMs);
  }, [departureDate, nowMs, myBus?.route_started_at]);

  const routeCompleteLabel = useMemo(() => {
    if (completingRoute) return 'Working…';
    if (hasWaitlist) return 'Route done — confirm waitlist';
    return 'Release bus';
  }, [completingRoute, hasWaitlist]);

  const canStartRoute = useMemo(() => {
    const odo = parseInt(startOdometer, 10);
    return !Number.isNaN(odo) && odo >= 0 && startOdometer.trim() !== '';
  }, [startOdometer]);

  const canCompleteRoute = useMemo(() => {
    const odo = parseInt(endOdometer, 10);
    return !Number.isNaN(odo) && odo >= 0 && endOdometer.trim() !== '';
  }, [endOdometer]);

  async function toggleOnline() {
    if (!profile?.id) return;
    setError('');
    const next = !profile.driver_online;

    const { error: err } = await supabase.from('profiles').update({ driver_online: next }).eq('id', profile.id);
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();

    if (!next) {
      const released = await releaseBusAssignment();
      if (!released) {
        await supabase.from('profiles').update({ driver_online: true }).eq('id', profile.id);
        await refresh();
        return;
      }
      setMyBus(null);
      setUnavailableBus(null);
      setGoingRows([]);
      setWaitlistRows([]);
      setActiveTripSlot(null);
      setNotifyMsg('You are offline. Bus assignment released.');
    } else {
      const { data, error: assignErr } = await supabase.rpc('try_auto_assign_driver');
      if (assignErr) {
        setError(assignErr.message);
      } else if (data?.assigned && data?.bus_label) {
        setNotifyMsg(`Assigned to ${data.bus_label}`);
      }
    }
    await load();
  }

  async function applyEta() {
    if (!myBus?.id || !profile?.org_id || !routeEnRoute) return;
    const mins = parseInt(etaMinutes, 10);
    if (Number.isNaN(mins) || mins < 0) {
      setError('Enter ETA as minutes from now (0 or more).');
      return;
    }
    setError('');
    const etaAt = new Date(Date.now() + mins * 60 * 1000).toISOString();
    const { error: err } = await supabase.from('bus_eta').upsert(
      {
        bus_id: myBus.id,
        org_id: profile.org_id,
        eta_at: etaAt,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'bus_id' },
    );
    if (err) {
      setError(err.message);
      return;
    }
    setEtaMinutes('');
    setNotifyMsg('ETA updated for passengers.');
  }

  async function startRoute() {
    if (!myBus?.id || !canStartRoute) return;
    if (myBus.route_started_at) return;
    setError('');
    setStartingRoute(true);
    try {
      const { error: err } = await supabase.rpc('driver_start_route', {
        p_odometer: parseInt(startOdometer, 10),
        p_trip_date: tripDate,
        p_trip_slot_id: activeTripSlot?.id ?? null,
      });
      if (err) {
        setError(err.message);
        return;
      }
      setStartOdometer('');
      setNotifyMsg('Route started — you can notify passengers and set ETA.');
      await load();
    } finally {
      setStartingRoute(false);
    }
  }

  async function notifyPassengers(minutes) {
    if (!myBus?.id || !routeEnRoute) return;
    setError('');
    setNotifyMsg('');
    setNotifying(true);
    try {
      const { error: err } = await supabase.from('buses').update({ current_eta_minutes: minutes }).eq('id', myBus.id);
      if (err) {
        setError(err.message);
        return;
      }
      setNotifyMsg('Passengers notified.');
    } finally {
      setNotifying(false);
    }
  }

  async function markPickedUp(row) {
    setError('');
    const { error: err } = await supabase.rpc('driver_mark_picked_up', { p_daily_going_id: row.id });
    if (err) {
      setError(err.message);
      return;
    }
    setNotifyMsg(`${row.displayName} marked picked up.`);
    await load();
  }

  async function sendIssue(e) {
    e.preventDefault();
    if (!issueMessage.trim() || sendingIssue) return;
    setError('');
    setSendingIssue(true);
    try {
      const { error: err } = await supabase.rpc('driver_send_issue', { p_message: issueMessage.trim() });
      if (err) {
        setError(err.message);
        return;
      }
      setIssueMessage('');
      setNotifyMsg('Message sent to admin.');
    } finally {
      setSendingIssue(false);
    }
  }

  async function releaseBusAssignment() {
    const { error: releaseErr } = await supabase.rpc('driver_release_bus');
    if (releaseErr) {
      setError(releaseErr.message);
      return false;
    }
    return true;
  }

  async function handleSignOut() {
    if (profile?.id) {
      await supabase.from('profiles').update({ driver_online: false }).eq('id', profile.id);
    }
    await releaseBusAssignment();
    await signOutUser();
    navigate('/login', { replace: true });
  }

  async function completeRoute() {
    if (!myBus?.id || !canCompleteRoute) return;
    setError('');
    setCompletingRoute(true);
    try {
      const { data, error: err } = await supabase.rpc('complete_route', {
        p_bus_id: myBus.id,
        p_trip_date: tripDate,
        p_end_odometer: parseInt(endOdometer, 10),
      });
      if (err) {
        setError(err.message);
        return;
      }
      setEndOdometer('');
      if (data?.released) {
        setNotifyMsg(
          data?.promoted
            ? `Route complete — ${data.promoted} waitlisted passenger(s) confirmed. Bus released.`
            : 'Bus released. Go online again when you need a new assignment.',
        );
      } else if (data?.promoted) {
        setNotifyMsg(`Route complete — ${data.promoted} waitlisted passenger(s) confirmed.`);
      } else if (data?.waitlist_remaining) {
        setNotifyMsg(`${data.waitlist_remaining} passenger(s) still waitlisted — bus stays assigned.`);
      } else {
        setNotifyMsg('Route updated.');
      }
      await load();
    } finally {
      setCompletingRoute(false);
    }
  }

  const profileName = useMemo(() => profile?.full_name || 'Driver', [profile?.full_name]);

  if (loading) {
    return (
      <div style={shell}>
        <p style={{ color: muted }}>Loading driver portal…</p>
      </div>
    );
  }

  return (
    <div style={shell}>
      <header style={headerBar}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.2rem, 4vw, 1.45rem)' }}>Driver</h1>
          <p style={{ margin: '4px 0 0', color: muted, fontSize: '0.9rem' }}>{profileName}</p>
        </div>
        <button type="button" onClick={handleSignOut} style={ghostBtn}>
          Sign out
        </button>
      </header>

      {error ? (
        <p style={{ color: '#f28b82', marginBottom: 12 }} role="alert">
          {error}
        </p>
      ) : null}

      {notifyMsg ? (
        <p style={{ color: '#81c995', marginBottom: 12, fontSize: '0.9rem' }} role="status">
          {notifyMsg}
        </p>
      ) : null}

      <section style={section}>
        <h2 style={h2}>Availability</h2>
        <div style={cardBox}>
          <p style={{ marginTop: 0, color: muted, fontSize: '0.9rem' }}>
            Go online to be auto-assigned to an available bus that matches your licence and serves passengers in an area.
          </p>
          <button type="button" onClick={toggleOnline} style={profile.driver_online ? dangerBtn : primaryBtn}>
            {profile.driver_online ? 'Go offline' : 'Go online'}
          </button>
        </div>
      </section>

      {unavailableBus ? (
        <section style={section}>
          <h2 style={h2}>Your bus</h2>
          <div style={{ ...cardBox, borderColor: 'rgba(242,139,130,0.35)' }}>
            <p style={{ margin: 0, color: '#f28b82', fontWeight: 600 }}>
              Bus {unavailableBus.label} is marked unavailable by admin — you are unassigned until it is available again.
            </p>
          </div>
        </section>
      ) : null}

      <section style={section}>
        <h2 style={h2}>Your bus</h2>
        {myBus ? (
          <div style={cardBox}>
            <strong style={{ fontSize: '1.05rem' }}>{myBus.label}</strong>
            <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
              {myBus.plate_number || 'No plate'}
              {myBus.service_area ? ` · Area: ${myBus.service_area}` : ''}
            </div>
            <div style={{ color: muted, fontSize: '0.85rem', marginTop: 6 }}>
              {myBus.bus_type === 'auto' ? 'Automatic' : 'Manual'} · {myBus.seat_capacity} seats
              {myBus.last_odometer != null ? ` · Last odometer: ${myBus.last_odometer}` : ''}
            </div>
            {activeTripSlot ? (
              <div style={{ color: muted, fontSize: '0.88rem', marginTop: 8 }}>
                Trip: <strong style={{ color: text }}>{activeTripSlot.label}</strong> at{' '}
                {formatTimeAmPm(activeTripSlot.start_time, tripDate)}
              </div>
            ) : null}

            {!routeEnRoute ? (
              <div style={{ marginTop: 16 }}>
                <label style={label}>Odometer reading (required to start)</label>
                <input
                  type="number"
                  min={0}
                  value={startOdometer}
                  onChange={(e) => setStartOdometer(e.target.value)}
                  style={{ ...input, maxWidth: 200, marginBottom: 12 }}
                  placeholder={myBus.last_odometer != null ? String(myBus.last_odometer) : 'e.g. 45230'}
                />
              </div>
            ) : null}

            {countdownText ? (
              <div style={{ marginTop: 8, marginBottom: 12 }}>
                {isDepartureTimeWindow ? (
                  <div
                    style={{
                      background: 'rgba(13,115,119,0.16)',
                      border: '1px solid rgba(13,115,119,0.55)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      marginBottom: 10,
                    }}
                    role="alert"
                  >
                    <strong style={{ display: 'block', fontSize: '1.1rem', color: text }}>Time to start your route</strong>
                  </div>
                ) : null}

                <div
                  style={{
                    fontSize: '2rem',
                    fontWeight: 900,
                    letterSpacing: '-0.02em',
                    color: accent,
                    marginBottom: 10,
                  }}
                >
                  {countdownText}
                </div>

                <button type="button" onClick={startRoute} style={primaryBtn} disabled={!!myBus.route_started_at || !canStartRoute || startingRoute}>
                  {startingRoute ? 'Starting…' : 'Start route'}
                </button>
              </div>
            ) : routeEnRoute ? (
              <p style={{ marginTop: 14, marginBottom: 0, color: '#81c995', fontSize: '0.9rem' }}>En route to passengers.</p>
            ) : null}

            {!routeEnRoute && myBus && !countdownText ? (
              <div style={{ marginTop: 8 }}>
                <button type="button" onClick={startRoute} style={primaryBtn} disabled={!canStartRoute || startingRoute}>
                  {startingRoute ? 'Starting…' : 'Start route'}
                </button>
                {!canStartRoute ? (
                  <p style={{ margin: '10px 0 0', color: muted, fontSize: '0.85rem' }}>
                    Enter the bus odometer reading before starting.
                  </p>
                ) : (
                  <p style={{ margin: '10px 0 0', color: muted, fontSize: '0.85rem' }}>
                    Start the route before notifying passengers or setting ETA.
                  </p>
                )}
              </div>
            ) : null}

            {routeEnRoute ? (
              <>
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>Notify passengers</h3>
                  <p style={{ margin: '0 0 12px', color: muted, fontSize: '0.88rem' }}>
                    Send a live alert while you are en route. Passengers see these on their dashboard.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[30, 10, 5, 0].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => notifyPassengers(mins)}
                        disabled={notifying}
                        style={primaryBtn}
                      >
                        {mins === 0 ? 'Arriving now' : `${mins} min away`}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <label style={label}>ETA (minutes from now)</label>
                  <p style={{ margin: '0 0 8px', color: muted, fontSize: '0.85rem' }}>
                    Optional countdown shown to confirmed passengers while you are en route.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <input
                      type="number"
                      min={0}
                      value={etaMinutes}
                      onChange={(e) => setEtaMinutes(e.target.value)}
                      style={{ ...input, maxWidth: 160 }}
                      placeholder="e.g. 25"
                    />
                    <button type="button" onClick={applyEta} style={primaryBtn}>
                      Update ETA
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <label style={label}>End odometer (required to finish trip)</label>
                  <input
                    type="number"
                    min={0}
                    value={endOdometer}
                    onChange={(e) => setEndOdometer(e.target.value)}
                    style={{ ...input, maxWidth: 200, marginBottom: 12 }}
                    placeholder="e.g. 45285"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" onClick={completeRoute} disabled={completingRoute || !canCompleteRoute} style={hasWaitlist ? primaryBtn : ghostBtn}>
                      {routeCompleteLabel}
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : !unavailableBus ? (
          <p style={{ color: muted }}>
            No bus assigned yet. Go online — the app will assign you when an available bus needs a driver matching your licence.
          </p>
        ) : null}
      </section>

      <section style={section}>
        <h2 style={h2}>Report an issue</h2>
        <form onSubmit={sendIssue} style={cardBox}>
          <p style={{ marginTop: 0, color: muted, fontSize: '0.88rem' }}>
            Send a message or update to your admin if something happens on the way.
          </p>
          <textarea
            value={issueMessage}
            onChange={(e) => setIssueMessage(e.target.value)}
            rows={3}
            required
            style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 12, resize: 'vertical' }}
            placeholder="e.g. Traffic delay on A1, running 15 minutes late…"
          />
          <button type="submit" disabled={sendingIssue || !issueMessage.trim()} style={primaryBtn}>
            {sendingIssue ? 'Sending…' : 'Send to admin'}
          </button>
        </form>
      </section>

      <section style={{ ...section, paddingBottom: 40 }}>
        <h2 style={h2}>Passengers to pick up</h2>
        {myBus ? (
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            Picking up <strong style={{ color: text }}>{pickupSummary.passengers}</strong> group
            {pickupSummary.passengers !== 1 ? 's' : ''} ({pickupSummary.people} people, {pickupSummary.seats} seats). Tap when picked up — they are removed from this trip and marked not going.
          </p>
        ) : (
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            Passengers appear here once you have an available bus assigned.
          </p>
        )}
        {goingRows.length === 0 ? (
          <p style={{ color: muted }}>{myBus ? 'No passengers waiting for pickup on this trip.' : '—'}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {goingRows.map((row) => (
              <div key={row.id} style={cardBox}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div>
                    <strong>
                      {row.displayName || 'Passenger'}
                      {row.isGuest ? <span style={{ color: muted, fontWeight: 400 }}> (guest)</span> : null}
                    </strong>
                    <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                      {row.pickupArea ? `${row.pickupArea}${row.pickupAddress ? ` · ${row.pickupAddress}` : ''}` : 'Pickup area not set'}
                      {' · '}
                      {row.seat_count ?? 1} seat{(row.seat_count ?? 1) !== 1 ? 's' : ''} ({row.party_total ?? 1} people)
                    </div>
                    {row.phone ? (
                      <div style={{ marginTop: 6 }}>
                        {phoneTelHref(row.phone) ? (
                          <a href={phoneTelHref(row.phone)} style={{ color: accent, fontWeight: 600, textDecoration: 'none' }}>
                            {row.phone}
                          </a>
                        ) : (
                          <span style={{ color: text }}>{row.phone}</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <button type="button" onClick={() => markPickedUp(row)} style={primaryBtn}>
                    Picked up
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {myBus && waitlistRows.length > 0 ? (
        <section style={{ ...section, paddingBottom: 40 }}>
          <h2 style={h2}>Waitlisted ({waitlistRows.length})</h2>
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            These passengers are waiting for a seat. Tap route done after pickups to confirm them on the bus.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {waitlistRows.map((row) => (
              <div key={row.id} style={cardBox}>
                <strong>{row.displayName}{row.isGuest ? ' (guest)' : ''}</strong>
                <div style={{ color: '#fbbc04', fontSize: '0.88rem', marginTop: 4 }}>
                  Waitlisted · {row.pickupArea || 'Area unknown'} · {row.seat_count ?? 1} seat{(row.seat_count ?? 1) !== 1 ? 's' : ''}
                  {row.phone && phoneTelHref(row.phone) ? (
                    <>
                      {' · '}
                      <a href={phoneTelHref(row.phone)} style={{ color: accent, textDecoration: 'none' }}>
                        {row.phone}
                      </a>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <div style={{ paddingBottom: 40 }} />
      )}
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: bg,
  color: text,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  padding: '20px 16px 40px',
  maxWidth: 640,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const headerBar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const section = { marginBottom: 24 };
const h2 = { fontSize: '1.1rem', margin: '0 0 10px' };

const cardBox = {
  background: card,
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.06)',
};

const label = { display: 'block', marginBottom: 6, fontSize: '0.8rem', color: muted };

const input = {
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '0.95rem',
};

const primaryBtn = {
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
};

const dangerBtn = {
  ...ghostBtn,
  borderColor: 'rgba(242,139,130,0.5)',
  color: '#f28b82',
};
